import { makeAutoObservable, reaction, runInAction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { debounce } from 'lodash'
import { type ICardFilter, showToast } from 'shared/ui'
import { TableStore } from 'shared/ui/Table'
import { logger, removeSpacesFromNumericString } from 'shared/lib'
import { type PageLayoutStore } from 'shared/layout'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { RequestLoadData } from 'shared/api/lib'
import type {
  IParamsGetBroadcastContacts,
  IResponseGetBroadcastContacts,
  IResponseOneTimeBroadcast,
} from 'entities/Broadcast/api/types'
import { BroadcastOneTime } from 'entities/Broadcast'
import { ContactStatistic } from 'entities/ContactStatistic'
import { BroadcastApi } from 'entities/Broadcast/api/broadcast'
import { type IFiltersListGroups } from 'entities/Contacts/api/types'
import { type IResponseFilterSegment } from 'entities/Segment'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type IOpenContactsDetails } from 'widgets/ContactsDetails'
import { ContactsDetailsManageStore } from 'widgets/ContactsDetails/store'
import { ContactsTagsModalStore } from 'widgets/ContactsTagsModal'

export class BroadcastDetailOneTimeStore {
  private readonly _broadcast: BroadcastOneTime
  private _contactsDetailsManageStore: ContactsDetailsManageStore
  private _page = 1
  private _limit = 10
  private _scrollToTOpTrigger = ''

  private _loadingContactsFiltersByGroups = false
  private _filtersLoadId = 0
  private _search = ''
  private _total = 0

  private _filter: IParamsGetBroadcastContacts['filter'] = 'sent_to'
  private _filtersListGroups: IFiltersListGroups | null = null
  private _filtersList: IResponseFilterSegment[] | null = null
  private _itemsMap: Map<string, ContactStatistic> = new Map<string, ContactStatistic>()
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParams: IReactionDisposer | null = null
  private _contactsTagsModalStore: ContactsTagsModalStore
  private _previousSearchValue = ''

  constructor(
    broadcastResponse: IResponseOneTimeBroadcast,
    private _pageLayoutStore: PageLayoutStore,
    private _entities: IEntitiesStore
  ) {
    this._broadcast = new BroadcastOneTime(broadcastResponse, this._entities)
    this._contactsDetailsManageStore = new ContactsDetailsManageStore(this._entities)
    this._contactsTagsModalStore = new ContactsTagsModalStore(this._entities)

    this.loadData()
    makeAutoObservable(this)
    this.reactionParamsWithoutPage()
    this.reactionParams()
  }

  loadBroadcastContactsFiltersByGroups = async () => {
    const loadId = ++this._filtersLoadId
    try {
      runInAction(() => {
        this._loadingContactsFiltersByGroups = true
      })
      const { data } = await BroadcastApi.getBroadcastContactsFiltersByGroups(this._broadcast.id, [
        this._filter,
      ])
      if (loadId !== this._filtersLoadId) return
      runInAction(() => {
        if (data.data.filtersListGroups) this._filtersListGroups = data.data.filtersListGroups
        if (data.data.filtersList) this._filtersList = data.data.filtersList
      })
    } catch (e) {
      logger.error(e)
      if (loadId === this._filtersLoadId && this._tableStore.bulkAllMode) {
        this._tableStore.unselectAllIds()
        showToast({
          title: "Couldn't load the selected contacts",
          desc: 'Something went wrong while preparing your selection. Please try selecting again.',
          type: 'error',
        })
      }
    } finally {
      if (loadId === this._filtersLoadId) {
        runInAction(() => {
          this._loadingContactsFiltersByGroups = false
        })
      }
    }
  }

  resetSelectAllFilters = () => {
    this._filtersListGroups = null
    this._filtersList = null
  }

  private _tableStore = new TableStore<ContactStatistic>({
    element: 'contact',
    withoutDefaultManageColumns: true,
    selectAllCallback: this.loadBroadcastContactsFiltersByGroups,
    resetSelectAllCallback: this.resetSelectAllFilters,
  })

  get pageLayoutStore() {
    return this._pageLayoutStore
  }

  get contactsTagsModalStore() {
    return this._contactsTagsModalStore
  }

  get broadcast() {
    return this._broadcast
  }

  get tableStore() {
    return this._tableStore
  }

  get limit() {
    return this._limit
  }

  get scrollToTOpTrigger() {
    return this._scrollToTOpTrigger
  }

  get page() {
    return this._page
  }

  get loading() {
    return this._requestLoadData.loading
  }

  get total() {
    return this._total
  }

  get search() {
    return this._search
  }

  reactionParams = () => {
    this._disposeParams?.()
    this._disposeParams = reaction(
      () => this.params,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._requestLoadData.setLoading(true)
        this._page = 1
      }
    )
  }

  reset = () => {
    this._disposeParams?.()
    this._disposeParamsWithoutPage?.()
    this._contactsDetailsManageStore.reset()
    this.broadcast.dispose()
    this._requestLoadData.dispose()
  }

  init = () => {
    this._contactsDetailsManageStore.init()
    this.broadcast.init()
  }

  get onOpenContactsDetails(): IOpenContactsDetails {
    return {
      disabled: this._contactsDetailsManageStore.disabled,
      sendFromType: this._broadcast.sendFrom?.type,
      open: (data) => {
        const isContactOwner = this._broadcast.sendFrom?.type === 'from_contact_owner'
        const hasInbox = !!this._entities.inboxesStore.getItem(data.inboxId)

        if (isContactOwner && !hasInbox) {
          modalStore.addModal({
            disabledOnAllClose: true,
            type: ModalTypeList.INFO,
            title: 'Content unavailable',
            id: 'content_unavailable',
            desc: "We're sorry, but you don't currently have access to this content or it was deleted. Please contact the user who shared the link with you to request access.",
          })
          return
        }

        this._contactsDetailsManageStore.onOpenContactDetails(
          {
            contactId: data.contactId,
            inboxId: this._broadcast.inboxId || data.inboxId,
          },
          'broadcast',
          false
        )
      },
    }
  }

  get contactsDetailsStore() {
    return this._contactsDetailsManageStore.contactsDetailsStore
  }

  get isNoData() {
    return !this.loading && !this._itemsMap.size && !this._search
  }

  get items(): ContactStatistic[] {
    return Array.from(this._itemsMap.values())
  }

  get paramsWithoutPage(): Omit<IParamsGetBroadcastContacts, 'page'> {
    return {
      search: removeSpacesFromNumericString(this._search),
      length: this._limit,
      filter: this._filter,
      sortBy: this._tableStore.sortBy || 'created_at',
      sortOrder: this._tableStore.sortOrder,
    }
  }

  get params(): IParamsGetBroadcastContacts {
    return {
      page: this._page,
      ...this.paramsWithoutPage,
    }
  }

  get name() {
    return this._broadcast.name
  }
  get status() {
    return this._broadcast.status
  }
  get type() {
    return this._broadcast.type
  }
  get failed_reason() {
    return this._broadcast.failed_reason
  }

  get statistics() {
    return this._broadcast.statistics
  }

  setLoading = (loading: boolean) => {
    this._requestLoadData.setLoading(loading)
  }

  onActiveFilter = (filter: ICardFilter<IParamsGetBroadcastContacts['filter']>) => {
    this._filter = filter.key
    this.resetSelectedContacts()
  }

  resetSelectedContacts = () => {
    this.resetSelectAllFilters()
    this._tableStore.unselectAllIds()
  }

  private setData = (data: IResponseGetBroadcastContacts) => {
    this._itemsMap.clear()

    data.data.forEach((item) => {
      const uniqueId = `${item.id}-${item.conversation_id}-${item.number || 0}`
      this._itemsMap.set(uniqueId, new ContactStatistic(item))
    })

    this._total = data.total
  }

  private _debouncedTrackSearchActivity = debounce((searchQuery: string) => {
    this._entities.organizationStore.handleTrackOrganizationActivity({
      event: 'phi.contact.searched',
      data: {
        search_query: searchQuery,
      },
    })
  }, 2000)

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      const { data } = await BroadcastApi.getBroadcastContacts(this._broadcast.id, this.params, {
        cancelToken: cancelRequest.token,
      })

      if (
        this.params.search !== this._previousSearchValue &&
        this._entities.organizationStore.isActivityLoggingEnabled
      ) {
        this._previousSearchValue = this.params.search
        this._debouncedTrackSearchActivity(this.params.search)
      }

      this.setData(data)
      this._scrollToTOpTrigger = nanoid()
    },
  })

  private loadData = async () => {
    return this._requestLoadData.loadData()
  }

  setSearch = (value: string) => {
    this._search = value
  }

  onPaginationModelChange = (page: number, limit: number) => {
    this._page = page
    this._limit = limit
  }

  get filters() {
    if (!this.statistics) return []
    let filters: ICardFilter<IParamsGetBroadcastContacts['filter']>[] = this.statistics?.filters
    if (this._broadcast.type === 'ringless') {
      const ringlessColumnsIds = [
        'sent_to',
        'success',
        'skipped',
        'replied',
        'no_replies',
        'failed',
        'in_progress',
        'quiet_hours',
      ]
      filters = this.statistics?.filters?.filter((column) =>
        this._broadcast.type === 'ringless' ? ringlessColumnsIds.includes(column.key) : true
      ) as ICardFilter<IParamsGetBroadcastContacts['filter']>[]
    }

    if (
      this._broadcast.status === 'pause' ||
      this._broadcast.status === 'stopped' ||
      this._broadcast.status === 'restrict_hours_paused'
    ) {
      return filters
    }
    return filters.filter((filter) => filter.key !== 'skipped')
  }

  get filter() {
    return this._filter
  }

  get hasNoReplies() {
    return this._filter === 'no_replies'
  }

  onSuccessDelete = () => {
    this.loadData()
    this._tableStore.unselectAllIds()
  }

  get loadingContactsFiltersByGroups() {
    return this._loadingContactsFiltersByGroups
  }

  get filtersListGroups() {
    return this._filtersListGroups
  }

  get filtersList() {
    return this._filtersList
  }
}
